import React from "react";
import { Loader2 } from "lucide-react";

/**
 * HeaderIconButton
 *
 * Shared icon button used in every top-right header action area.
 * Goals:
 *   - consistent hit area (40×40) and corner radius across the app
 *   - consistent unread badge placement & style
 *   - clear tap feedback (scale + bg) so the user knows it registered
 *   - optional loading spinner overlay when the parent is fetching
 *   - optional visual grouping via `grouped` so related actions cluster
 *
 * Do not inline bespoke icon-button styles elsewhere — always use this.
 */
export default function HeaderIconButton({
    icon: Icon,
    onClick,
    to, // optional — passed to NavLink/Link when as={NavLink} or as={Link}
    label,
    title,
    testId,
    badgeCount = 0,
    badgeColor = "violet", // "violet" | "emerald" | "amber" | "rose"
    variant = "default", // "default" | "subtle"
    disabled = false,
    loading = false,
    className = "",
    as: As = "button",
    children,
    ...rest
}) {
    const linkProps = to ? { to } : {};
    const baseBg =
        variant === "subtle"
            ? "bg-transparent hover:bg-white/10 active:bg-white/20"
            : "bg-white/8 hover:bg-white/14 active:bg-white/22";

    const badgeBg =
        {
            violet: "bg-violet-500",
            emerald: "bg-emerald-500",
            amber: "bg-amber-500",
            rose: "bg-rose-500",
        }[badgeColor] || "bg-violet-500";

    const Component = As;
    return (
        <Component
            onClick={onClick}
            disabled={disabled || loading}
            aria-label={label || title}
            aria-busy={loading || undefined}
            title={title || label}
            data-testid={testId}
            className={`relative inline-flex items-center justify-center w-10 h-10 rounded-xl ${baseBg} transition-all duration-150 ease-out active:scale-[0.88] hover:scale-[1.04] disabled:opacity-40 disabled:active:scale-100 select-none ${className}`}
            {...linkProps}
            {...rest}
        >
            {loading ? (
                <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
            ) : Icon ? (
                <Icon className="w-5 h-5 text-gray-300" />
            ) : (
                children
            )}
            {!loading && badgeCount > 0 && (
                <span
                    className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 ${badgeBg} rounded-full flex items-center justify-center text-[10px] font-bold text-white leading-none shadow-md`}
                    aria-label={`${badgeCount} unread`}
                >
                    {badgeCount > 99 ? "99+" : badgeCount}
                </span>
            )}
        </Component>
    );
}

/**
 * HeaderActionsCluster
 *
 * Thin visual grouping wrapper used to cluster related header actions
 * into one status group, kept distinct from the profile avatar.
 */
export function HeaderActionsCluster({ children, className = "" }) {
    return (
        <div
            className={`inline-flex items-center gap-0.5 p-0.5 rounded-2xl bg-white/[0.04] border border-white/5 ${className}`}
        >
            {children}
        </div>
    );
}
